<template>
  <component :is="elementType" class="question__answer-item" @click="onSelectedAnswer(answer)">
    <div class="question__answer-item-wrapper" :style="answerWrapperStyles">
      <span class="question__answer-item-label">
        <i
          v-if="answerLayout?.designType === AnswerDesignTypes.CUSTOM"
          class="question__answer-item-checkmark form-check-input fa fa-check"
          aria-hidden="true"
          :style="checkmarkStyles"
        ></i>
        <i
          v-if="answerLayout?.designType === AnswerDesignTypes.RADIOBUTTONS"
          class="question__answer-item-radio form-check-input"
          :style="{ backgroundColor: answerLayout?.circleColor }"
        >
          <i
            class="question__answer-item-radio-icon"
            :style="{ backgroundColor: answerLayout?.checkedCircleColor }"
          ></i>
        </i>
        {{ answer.value }}
      </span>
      <slot />
    </div>
    <AnswerTooltip v-if="answer.paused">{{ tooltip }}</AnswerTooltip>
  </component>
</template>

<script lang="ts">
import type { CSSProperties, PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import AnswerTooltip from './AnswerItemTooltip.vue';
import type { GamePollAnswerState } from '@/src/components/games/poll/Model';
import type { AnswerLayoutSettingsState } from '@/src/models/settings/AnswerLayoutSettingsModel';
import { AnswerDesignTypes, AnswerFontTypes } from '@/src/models/settings/AnswerLayoutSettingsModel';

type FontType = 'p' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface RadioButtonStyles {
  circleColor: CSSProperties;
  checkedColor: CSSProperties;
}

export default defineComponent({
  name: 'AnswerTextItem',
  components: { AnswerTooltip },
  props: {
    answer: {
      type: Object as PropType<GamePollAnswerState>,
      required: true
    },
    answerLayout: {
      type: Object as PropType<AnswerLayoutSettingsState>
    },
    tooltip: {
      type: String
    }
  },
  emits: ['selectedAnswer'],
  setup(props, context) {
    const onSelectedAnswer = (answer: GamePollAnswerState) => {
      if (!props.answer.paused) {
        context.emit('selectedAnswer', answer);
      }
    };

    const radioButtonStyling = computed<RadioButtonStyles>(() => {
      return {
        circleColor: { backgroundColor: props.answerLayout?.circleColor || '#ffffff' },
        checkedColor: { backgroundColor: props.answerLayout?.checkedCircleColor || '#000000' }
      };
    });

    const elementType = computed(() => {
      let fontType: FontType | undefined;
      switch (props.answerLayout?.fontType) {
        case AnswerFontTypes.P:
          fontType = 'p';
          break;

        case AnswerFontTypes.H2:
          fontType = 'h2';
          break;

        case AnswerFontTypes.H3:
          fontType = 'h3';
          break;

        case AnswerFontTypes.H5:
          fontType = 'h5';
          break;

        case AnswerFontTypes.H6:
          fontType = 'h6';
          break;

        default:
          fontType = 'h4';
      }
      return fontType;
    });

    const checkmarkStyles = computed(() => {
      return {
        backgroundColor: props.answerLayout?.circleColor,
        color: props.answerLayout?.checkmarkColor
      };
    });

    const answerWrapperStyles = computed<CSSProperties>(() => {
      if (props.answerLayout?.enableAnswerOpacity) return {};

      return {
        opacity: '1'
      };
    });

    return {
      onSelectedAnswer,
      AnswerDesignTypes,
      checkmarkStyles,
      elementType,
      radioButtonStyling,
      answerWrapperStyles
    };
  }
});
</script>
